import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormVariantLabelService } from './form-variant-label.service';

/**
 * Conditional Form Groups — fill-time variant selector.
 *
 * Renders an ng-select dropdown above a form-fill wrapper. The host fill UI (e.g.
 * `session-form-page-steps`) drops this in just above its wizard, listens to
 * `(variantChange)`, and uses `applyVariantVisibilityToFormGroup` to update field
 * visibility + validators on each change.
 *
 * Behaviour:
 * - On init, if no `selectedVariantGuid` is supplied and a variant has `isDefault = true`,
 *   it auto-selects that variant and emits `variantChange`.
 * - When nothing is selected and no default exists, the host should hide its wizard until
 *   the user picks a variant (the selector itself doesn't gate the form — the host does).
 * - Reusable across every form-fill UI (Phase D rollouts).
 *
 * OnPush — selection state is the only thing that changes, and we drive that via
 * markForCheck on user interaction.
 */
export interface FormVariantOption {
  variantGuid: string;
  title: string;
  description?: string;
  sortOrder: number;
  isDefault: boolean;
}

@Component({
  selector: 'app-form-variant-selector',
  templateUrl: './form-variant-selector.component.html',
  styleUrls: ['./form-variant-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FormVariantSelectorComponent implements OnInit, OnChanges, OnDestroy {
  /** Variants defined on the loaded form. Empty array hides the selector entirely. */
  @Input() variants: FormVariantOption[] = [];

  /**
   * Pre-selected variant guid (e.g. when reloading a saved answer that already chose a
   * variant). Null/undefined means "no selection yet" — the component will honour any
   * variant marked `isDefault` and emit a change on init.
   */
  @Input() selectedVariantGuid: string | null = null;

  /** Default label when none is admin-defined for the form. */
  private static readonly DEFAULT_LABEL = 'Form variant';

  /** Explicit label override. If set, takes precedence over anything self-fetched via formGuid. */
  @Input() set label(value: string | null | undefined) {
    this._explicitLabel = value ?? null;
  }
  get label(): string {
    return this._explicitLabel || this._resolvedLabel || FormVariantSelectorComponent.DEFAULT_LABEL;
  }
  private _explicitLabel: string | null = null;
  /** Label fetched via formGuid lookup. Used only when no explicit label is set. */
  private _resolvedLabel: string | null = null;

  /**
   * When set, the selector self-fetches the admin-defined variant label for this form
   * (cached client-side via FormVariantLabelService). The host doesn't need to plumb
   * the label down explicitly — pass the formGuid you already have and the right label
   * shows up automatically.
   */
  @Input() formGuid: string | null = null;

  /** Optional placeholder text inside the dropdown. Empty by default — the label above
   *  and help text below already make the prompt clear. */
  @Input() placeholder = '';

  /** Disable the selector (e.g. while saving). */
  @Input() disabled = false;

  /**
   * Variant pinning: true when the selection was supplied by a Program/Activity pin rather
   * than the user. The selector renders nothing at all - users are never prompted (or able)
   * to change paperwork an admin has configured, and the variant title carries no
   * information at fill time.
   */
  @Input() pinned = false;

  /** Emitted whenever the selection changes, including the initial default auto-select. */
  @Output() variantChange = new EventEmitter<string | null>();

  /**
   * Unique per instance so two selectors on one page (e.g. a contact-level and a form-level
   * variant in the same Record Session modal) never share an element id. A hardcoded id meant
   * the label->input link and ng-select's aria wiring pointed at the first instance in the DOM,
   * so clicking could land on the wrong selector.
   */
  private static instanceCount = 0;
  public readonly selectorId = `form-variant-selector-${++FormVariantSelectorComponent.instanceCount}`;

  private labelSub?: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    private labelService: FormVariantLabelService,
  ) {}

  ngOnInit(): void {
    this.setOrderedVariants();
    this.autoSelectDefaultIfNeeded();
    this.fetchLabelIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the variants list changes (e.g. form re-loaded) AND we have no selection, try the
    // default again. Keeps the host simple — it doesn't need to manage default logic.
    if (changes['variants'] && !changes['variants'].firstChange) {
      this.setOrderedVariants();
      this.autoSelectDefaultIfNeeded();
    }
    if (changes['formGuid'] && !changes['formGuid'].firstChange) {
      this.fetchLabelIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.labelSub?.unsubscribe();
  }

  private fetchLabelIfNeeded(): void {
    // Explicit override wins — no need to fetch.
    if (this._explicitLabel) return;
    if (!this.formGuid) return;
    this.labelSub?.unsubscribe();
    this.labelSub = this.labelService.getLabel(this.formGuid).subscribe(label => {
      this._resolvedLabel = label;
      this.cdr.markForCheck();
    });
  }

  /**
   * Sorted, MEMOISED view used by the template. This MUST be a stable array reference.
   * It was previously a getter returning a fresh `.slice().sort()` on every change-detection
   * cycle, so ng-select saw a "new" items list each CD and rebuilt its option nodes. When that
   * rebuild landed during an option click it removed the node mid-handler — Chromium throws
   * "Node cannot be found in the current page" and the selection silently failed (Firefox
   * tolerated the removed node, which is why it only broke on Edge/Chrome). Recompute only when
   * the `variants` input actually changes.
   */
  public orderedVariants: FormVariantOption[] = [];

  private setOrderedVariants(): void {
    this.orderedVariants = (this.variants ?? [])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  public onSelectionChange(variantGuid: string | null): void {
    const next = variantGuid ?? null;
    if (next === this.selectedVariantGuid) return;
    this.selectedVariantGuid = next;
    this.variantChange.emit(next);
    this.cdr.markForCheck();
  }

  /** "a" / "an" picker for the help text — keeps the prompt grammatical when the
   *  admin renames the label (e.g. "an Activity", "a Programme"). */
  public articleFor(word: string): string {
    const first = (word ?? '').trim().charAt(0).toLowerCase();
    return ['a', 'e', 'i', 'o', 'u'].includes(first) ? 'an' : 'a';
  }

  /** With exactly one variant there is nothing to choose - hide the selector once it's applied. */
  public get soleVariantApplied(): boolean {
    return this.orderedVariants.length === 1 && !!this.selectedVariantGuid;
  }

  private autoSelectDefaultIfNeeded(): void {
    if (this.selectedVariantGuid) return;
    // A sole variant is auto-used with no selection step (1 option = automatic), matching
    // how variant-less forms behave; otherwise honour the form's IsDefault variant.
    const variants = this.variants ?? [];
    const def = variants.find(v => v.isDefault) ?? (variants.length === 1 ? variants[0] : undefined);
    if (!def) return;
    // Defer the emission to the next microtask so the parent component's CD cycle
    // has finished before we push the new value back up. Without this Angular
    // throws NG0100 (ExpressionChangedAfterItHasBeenCheckedError) because the
    // parent reads `selectedVariantGuid` as null on its first CD pass and then
    // we update it synchronously in init.
    queueMicrotask(() => {
      this.selectedVariantGuid = def.variantGuid;
      this.variantChange.emit(def.variantGuid);
      this.cdr.markForCheck();
    });
  }
}
